import React, { useState, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useForm, useWatch, useFormState, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory, Prompt } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';

import PriceInput from 'components/PriceInput';
import DashboardContainer from 'components/DashboardContainer';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import TextArea from 'components/TextArea';
import UploadButton from 'components/UploadButton';
import Preview from 'components/FundraiserPreview';
import CountrySelect from 'components/CountrySelect';
import Tooltip from 'components/Tooltip';
import { showErrorMessage } from 'redux/slices/notification';

import { fundraiserNew as TEXT } from 'locales/en';
import { createFundraiser, editFundraiser } from 'redux/slices/fundraiser';
import { priceForAPI } from 'utils/price';
import schema, {
  FIELD_NAMES,
  defaultValues as _defaultValues,
  AMOUNT_OPTIONS,
} from './schema';
import { BASE_URL } from 'utils/constants';
import PATHS from 'routes/paths';
import { useMediaQuery } from 'hooks';
import breakpoints from 'utils/breakpoints';

import 'utils/vapor';

const FundraiserNew = ({ details, id }) => {
  const [progress, setProgress] = useState(0);
  const [imagePath, setImagePath] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();
  const isLarge = useMediaQuery(breakpoints.lg);
  const defaultValues = details || _defaultValues;
  const { register, handleSubmit, control, setValue, trigger } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { errors, isSubmitting, isDirty, dirtyFields } = useFormState({
    control,
  });

  const [name, goal, description, image, _city] = useWatch({
    control,
    name: [
      FIELD_NAMES.name,
      FIELD_NAMES.goal,
      FIELD_NAMES.description,
      FIELD_NAMES.image,
      FIELD_NAMES.city,
    ],
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      history.push(PATHS.fundraisers);
    }
  }, [history, isSubmitSuccessful]);

  useEffect(() => {
    if (details) {
      setImagePath(details.image.path);
      setImageSrc(details.image.path);
    }
  }, [details]);

  const onSubmit = async (data) => {
    const amount_options = AMOUNT_OPTIONS.map((item, idx) =>
      priceForAPI(data[`amount_options_${idx}`])
    );

    const {
      image: _image,
      amount_options_0,
      amount_options_1,
      amount_options_2,
      amount_options_3,
      ..._data
    } = data;

    try {
      if (imagePath) {
        if (details && id) {
          /* editing fundraiser */
          if (!_isEmpty(dirtyFields) || !!_image.lastModified) {
            const payload = Object.fromEntries(
              Object.keys(dirtyFields).map((key) => [key, data[key]])
            );
            const {
              amount_options_0,
              amount_options_1,
              amount_options_2,
              amount_options_3,
              ..._payload
            } = payload;
            if (!!_image.lastModified) {
              // means image newly uploaded from user's device
              _payload.image = imagePath;
              _payload.image_filename = image.name;
            }
            delete _payload[FIELD_NAMES.country];
            if (payload[FIELD_NAMES.country]) {
              _payload.country = payload[FIELD_NAMES.country].name;
            }
            return dispatch(
              editFundraiser({ id, amount_options, ..._payload })
            ).then((action) => {
              if (action.type.endsWith('fulfilled')) {
                setIsSubmitSuccessful(true);
              }
            });
          } else {
            return setIsSubmitSuccessful(true);
          }
        } else {
          delete _data[FIELD_NAMES.country];
          return dispatch(
            createFundraiser({
              ..._data,
              image: imagePath,
              image_filename: image.name,
              country: data[FIELD_NAMES.country].name,
              amount_options,
            })
          ).then((action) => {
            if (action.type.endsWith('fulfilled')) {
              setIsSubmitSuccessful(true);
            }
          });
        }
      }
    } catch (error) {
      return;
    }
  };

  const handleDeleteFile = () => {
    setProgress(0);
    setImageSrc(null);
    setImagePath(null);
    setValue(FIELD_NAMES.image, null);
  };

  const handleSelectFile = async (_file) => {
    const imageUrl = URL.createObjectURL(_file);
    setProgress(0);
    setImageSrc(null);
    setImagePath(null);

    setValue('image', _file, { shouldValidate: true });
    await trigger('image');

    if (errors.image?.message) return;

    const response = await Vapor.store(_file, {
      baseURL: BASE_URL,
      options: {
        withCredentials: true,
      },
      progress: (progress) => {
        setProgress(Math.round(progress * 100));
      },
    });

    if (!!response.key) {
      setImagePath(response.key);
      setImageSrc(imageUrl);
    } else {
      dispatch(showErrorMessage('Image upload failed'));

      setValue(FIELD_NAMES.image, null);
      setProgress(0);
      setImageSrc(null);
      setImagePath(null);
    }
  };

  const imagePlaceholder = useMemo(() => {
    return image?.name ?? '';
  }, [image]);

  const header = useMemo(() => {
    if (details) {
      return details.name;
    } else {
      return TEXT.header1;
    }
  }, [details]);

  const header2 = useMemo(() => {
    if (details) {
      return TEXT.editHeader2;
    } else {
      return TEXT.header2;
    }
  }, [details]);

  const CTA = useMemo(() => {
    if (details) {
      return TEXT.editCTA;
    } else {
      return TEXT.submit;
    }
  }, [details]);

  return (
    <DashboardContainer header={header} subheader={header2}>
      <Prompt
        when={isDirty && !isSubmitSuccessful}
        message={TEXT.leavePrompt}
      />
      <div className="flex min-h-screen-70 w-full flex-col rounded-lg bg-white p-4 py-10 shadow-md lg:flex-row lg:p-8 xl:p-12">
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          className="mr-4 w-full lg:w-7/12"
        >
          <FormInput
            label={TEXT.name}
            name={FIELD_NAMES.name}
            register={register}
            error={errors[FIELD_NAMES.name]?.message}
          />
          <PriceInput
            label={TEXT.goal}
            name={FIELD_NAMES.goal}
            control={control}
            inputPrefix="€"
            error={errors[FIELD_NAMES.goal]?.message}
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
          />
          <CountrySelect
            name={FIELD_NAMES.country}
            label={TEXT.country}
            control={control}
            error={errors[FIELD_NAMES.country]?.name?.message}
            inputBelowLabel={false}
          />
          <FormInput
            label={TEXT.city}
            name={FIELD_NAMES.city}
            register={register}
            error={errors[FIELD_NAMES.city]?.message}
          />
          <TextArea
            label={TEXT.description}
            name={FIELD_NAMES.description}
            register={register}
            error={errors[FIELD_NAMES.description]?.message}
            hasLimit
            count={description.length}
            maxLength={140}
          />
          <TextArea
            label={TEXT.story}
            name={FIELD_NAMES.long_description}
            register={register}
          />
          <Controller
            control={control}
            name={FIELD_NAMES.image}
            render={() => (
              <UploadButton
                label={
                  <div className="flex flex-row items-center">
                    {TEXT.image}
                    <Tooltip text={TEXT.image_tooltip} />
                  </div>
                }
                name={FIELD_NAMES.image}
                onSelectFile={handleSelectFile}
                onDeleteFile={handleDeleteFile}
                placeholder={imagePlaceholder}
                error={errors[FIELD_NAMES.image]?.message}
                progress={progress}
              />
            )}
          />
          <TextArea
            label={
              <div className="flex flex-row items-center">
                {TEXT.video}
                <Tooltip text={TEXT.video_tooltip} />
              </div>
            }
            name={FIELD_NAMES.video_embed}
            register={register}
          />
          {AMOUNT_OPTIONS.map((item, idx) => (
            <PriceInput
              key={idx}
              label={idx === 0 ? TEXT.amounts : ''}
              name={`amount_options_${idx}`}
              defaultValue={item}
              control={control}
              inputPrefix="€"
              error={errors[`amount_options_${idx}`]?.message}
              thousandSeparator
              fixedDecimalScale
              decimalScale={2}
            />
          ))}
          <Button
            className="btn-primary ml-auto block h-12 w-56 px-0 lg:w-48"
            type="submit"
            title={CTA}
            showSpinner={isSubmitting}
            isDisabled={isSubmitting || (!!progress && progress < 100)}
          />
        </form>
        {isLarge && (
          <Preview
            image={imageSrc}
            name={name}
            description={description}
            goal={goal}
            city={_city}
            raised="0"
          />
        )}
      </div>
    </DashboardContainer>
  );
};

FundraiserNew.propTypes = {
  id: PropTypes.string,
  details: PropTypes.shape({}),
};

export default memo(FundraiserNew);
